import { withVisibility } from "@/components/with-visibility/with-visibility";

import PageWrapper from "@/components/PageWrapper";
import { LoadingMask } from "@/components/loading-mask";
import { RegistrationEmailSentView } from "@/components/registration/email-sent-page";
import EventRegistration, {
  EmailFieldSet,
} from "@/components/registration/event-registration";
import { PrivacyPolicyTermsOfServiceFooter } from "@/components/registration/privacy-policy-terms-of-service";
import { useMaybeRouterVariables } from "@/components/routing-utils/useRouterVariables";
import { TextView } from "@/components/text-input/text-input";
import { toValidLuxonDateTime } from "@/components/utils/dates/to-luxon-datetime";
import { useButMightBeEmpty } from "@/components/utils/maybeFromNullable";
import { printDevError } from "@/components/utils/print-dev-error";
import { FieldSet, useFieldSet } from "@/components/utils/useFieldSet";
import {
  ServerDeadlineStates,
  useServerTimeDeadline,
} from "@/components/utils/useServerTime";
import { Button } from "@/flokit/core/button/button";
import { showToast } from "@/flokit/core/toast/show-toast";
import {
  domesticTimezoneIanaToCode,
  getTimeZoneIana,
} from "@/flokit/utils/date-time/domestic-timezone-iana";
import { DateTime as LDateTime } from "luxon";
import {
  AmILoggedInForwardResponse,
  PublicEventResponse,
  getAmILoggedInForwardQueryKey,
  useAmILoggedInForward,
  useFetchOrgColor,
  useFetchOrgDisplayName,
  useFetchOrgLogoLinkv2,
  useGetEventPublicById,
  useRegisterCandidateForEvent,
  useSendSigninMagicLinkEventRegistration,
} from "modules/api-docs";
import { RegistrationWrapper } from "modules/serendipitous/components/registration-wrapper";
import { Maybe, Nothing } from "purify-ts";
import { useState } from "react";
import { FiCheckCircle } from "react-icons/fi";
import { useQueryClient } from "react-query";
import { emailCollectionSchema } from "./schemas/email-collection.schema";

const RegistrationInformationFragment = withVisibility(
  (props: {
    onSubmit: (request: { email: string }) => void;
    fs: FieldSet<{
      email: string;
    }>;
    logo: string;
    orgName: string;
    orgFriendlyName: string;
    event: Maybe<PublicEventResponse>;
    registrationStartState: ServerDeadlineStates;
    registrationDeadlineState: ServerDeadlineStates;
    isSubmitting: boolean;
    enforceRegistrationDeadline: boolean;
    forwardLoggedInEmail: string | null;
  }) => {
    const fs = props.fs;
    const { event, registrationDeadlineState, registrationStartState } = props;

    const rangeType = event
      .chain((e) => {
        const startTime = toValidLuxonDateTime(e.startDateTime);
        const endTime = toValidLuxonDateTime(e.endDateTime);

        if (startTime.isJust() && endTime.isJust()) {
          const s = startTime.extract();
          const e = endTime.extract();
          if (s.hasSame(e, "day")) {
            return Maybe.of("time");
          } else {
            return Maybe.of("date");
          }
        }
        return Nothing;
      })
      .orDefault("date");

    const registrationDeadline = event
      .chain((e) => toValidLuxonDateTime(e.registrationEndDateTime))
      .orDefault(null);

    const isRegistrationOpen =
      (registrationDeadlineState === "before" &&
        registrationStartState === "after") ||
      !props.enforceRegistrationDeadline;

    const [hasChosenDifferentEmail, setHasChosenDifferentEmail] =
      useState(false);

    const registrationClosedMessage =
      registrationStartState === "before" ? (
        event
          .chain((e) => toValidLuxonDateTime(e.registrationStartDateTime))
          .map((dt) => (
            <span key="a" className="leading-10">
              Registration is closed. It will open on:
              <br />
              {dt.toLocaleString(LDateTime.DATETIME_SHORT)}&nbsp;
              {domesticTimezoneIanaToCode?.[dt.zoneName] ?? ""}.
            </span>
          ))
          .orDefault(
            <>
              Registration is closed. Please contact the organizer for more
              information.
            </>,
          )
      ) : (
        <>Registration is closed.</>
      );

    return (
      <>
        <EventRegistration
          logo={props.logo}
          orgName={props.orgName}
          description={props.event.map((event) => event.description).extract()}
          showTimeOnDateRange
          title={event.map((e) => e.name).orDefault("")}
          startTime={event
            .chain((e) => toValidLuxonDateTime(e.startDateTime))
            .orDefault(null)}
          endTime={event
            .chain((e) => toValidLuxonDateTime(e.endDateTime))
            .orDefault(null)}
          rangeType={rangeType as "time" | "date"}
          registrationDeadline={registrationDeadline}
          registrationMessage={
            registrationDeadline ? (
              <p className="text-center font-inter text-base text-medium-hippo">
                If you wish to participate, please register before
                <br />
                <span className="font-semibold">
                  {registrationDeadline?.toLocaleString(LDateTime.DATE_SHORT)}
                </span>
                &nbsp; at &nbsp;
                <span className="font-semibold">
                  {`${registrationDeadline?.toLocaleString(
                    LDateTime.TIME_SIMPLE,
                  )} ${getTimeZoneIana(registrationDeadline)}`}
                </span>
                .
              </p>
            ) : null
          }
          registrationClosedMessage={registrationClosedMessage}
          isRegistrationOpen={isRegistrationOpen}
        >
          {props.forwardLoggedInEmail && !hasChosenDifferentEmail ? (
            <LoggedIn
              email={props.forwardLoggedInEmail}
              onDifferentEmailRegisterClick={() =>
                setHasChosenDifferentEmail(true)
              }
              orgFriendlyName={props.orgFriendlyName}
              eventId={props.event
                .map((event) => event.id)
                // Shouldn't happen
                .orDefault(-1)}
            />
          ) : (
            <EmailSubmissionFragment
              fieldSet={fs}
              isLoading={props.isSubmitting}
              onSubmit={() => {
                props.onSubmit({
                  email: fs.values.email,
                });
              }}
              errorMsg={""}
            />
          )}
        </EventRegistration>
      </>
    );
  },
);

export const EmailSubmissionFragment = withVisibility(
  (props: {
    fieldSet: FieldSet<EmailFieldSet>;
    onSubmit: () => void;
    errorMsg?: string;
    isLoading: boolean;
  }) => {
    const { fieldSet, onSubmit, errorMsg, isLoading } = props;

    const errMsg: string =
      errorMsg ||
      'Please enter a valid email address (e.g. "user@example.com")';

    return (
      <>
        <TextView
          label={"Email"}
          type="text"
          name="email"
          placeholder="Enter your email address"
          required
          value={fieldSet.values["email"]}
          onChange={fieldSet.curriedOnChange("email")}
          validation={
            errorMsg !== "" || (fieldSet.isDirty.email && !fieldSet.hasAllValid)
          }
          //validation = has validation errors
          validationMsg={errMsg}
          onBlur={fieldSet.onBlur("email")}
          onKeyDown={(evt) => {
            if (evt.key === "Enter") {
              onSubmit();
            }
          }}
        />
        <Button
          color="navy"
          UNSAFE_className="mt-6"
          width="full"
          onClick={() => onSubmit()}
          loading={isLoading}
          aria-describedby="privacyPolicyTermsOfServiceFooter"
        >
          Next
        </Button>
        <PrivacyPolicyTermsOfServiceFooter />
      </>
    );
  },
);
const EmailSendFragment = withVisibility(RegistrationEmailSentView);

export const GenericEventEmailCollectionPage = () => {
  const rVars = useMaybeRouterVariables({
    org: "string",
    eid: "number",
  });

  const eventResponse = useGetEventPublicById(
    rVars.org.extract(),
    rVars.eid.extract(),
    { query: { enabled: rVars.org.isJust() && rVars.eid.isJust() } },
  );

  const logoResponse = useFetchOrgLogoLinkv2(
    {
      orgFriendlyName: rVars.org.orDefault(""),
    },
    {
      query: {
        enabled: rVars.org.isJust(),
      },
    },
  );

  const orgNameResponse = useFetchOrgDisplayName(
    {
      orgFriendlyName: rVars.org.orDefault(""),
    },
    {
      query: {
        enabled: rVars.org.isJust(),
      },
    },
  );

  const orgColorResponse = useFetchOrgColor(
    {
      orgFriendlyName: rVars.org.orDefault(""),
    },
    {
      query: { enabled: rVars.org.isJust() },
    },
  );

  const logo = useButMightBeEmpty(logoResponse.data);
  const orgName = useButMightBeEmpty(orgNameResponse.data);
  const event = useButMightBeEmpty(eventResponse.data);
  const orgColor = useButMightBeEmpty(orgColorResponse.data);

  const enforceRegistrationDeadline = event
    .map((e) => !!e.registrationEndDateTime)
    .orDefault(true);

  // If event does not have a registration start (e.g. non interview scheduling), this will always be "loading"
  const registrationStartState = useServerTimeDeadline({
    until: toValidLuxonDateTime(
      event
        .map(
          (e) =>
            e.registrationStartDateTime ??
            LDateTime.fromJSDate(new Date(0)).toISO(),
        )
        .extract(),
    ),
    enabled: event.isJust(),
  });

  const registrationDeadlineState = useServerTimeDeadline({
    until: toValidLuxonDateTime(
      event.map((e) => e.registrationEndDateTime).extract(),
    ),
    enabled: event.isJust(),
  });

  const [emailResponse, setEmailResponse] = useState<{
    emailSent: boolean;
  } | null>(null);

  const { mutate, isLoading } = useSendSigninMagicLinkEventRegistration({
    mutation: {
      onSuccess: () => {
        setEmailResponse({ emailSent: true });
      },
      onError: () => {
        showToast({
          type: "error",
          position: "top-right",
          message: "Email failed to send",
        });
        setEmailResponse({ emailSent: false });
      },
    },
  });

  const fs = useFieldSet(
    {
      email: "",
    },
    [],
    emailCollectionSchema,
  );

  const queryClient = useQueryClient();

  const {
    data: dataLoggedIn,
    isLoading: isLoadingLoggedIn,
    isFetched: isFetchedLoggedIn,
  } = useAmILoggedInForward({
    query: {
      // This query currently fails when the user is not logged in; we don't want it to keep trying, otherwise the loading spinner will hang
      retry: false,
      keepPreviousData: false,
      // This is necessary because the AmILoggedIn endpoint errors instead of returning success = false when the user is not logged in. This means that, if the user was logged into Forward when they landed on the page, but then changes tabs and logs out of Forward, the cache is not automatically updated because the endpoint errors instead of returning up-to-date information. So we need to manually overwrite the React Query cache
      onError: () => {
        if (
          queryClient.getQueryData<AmILoggedInForwardResponse>(
            getAmILoggedInForwardQueryKey(),
          )?.success
        ) {
          queryClient.setQueryData<AmILoggedInForwardResponse>(
            getAmILoggedInForwardQueryKey(),
            {
              success: false,
            },
          );
        }
      },
    },
  });
  const forwardLoggedInEmail = dataLoggedIn?.hasActiveAccount
    ? dataLoggedIn?.email
    : null;

  return (
    <PageWrapper
      title={event
        .map((e) => (e.name ? `${e.name} - Event Registration` : undefined))
        .orDefault("Event Registration")}
      unrestrictViewport={true}
    >
      <LoadingMask
        loading={
          eventResponse.isLoading ||
          registrationDeadlineState === "loading" ||
          (isLoadingLoggedIn && !isFetchedLoggedIn)
        }
      >
        <RegistrationWrapper orgColor={orgColor.orDefault("#3a6da3")}>
          <RegistrationInformationFragment
            enforceRegistrationDeadline={enforceRegistrationDeadline}
            hidden={emailResponse && emailResponse.emailSent}
            onSubmit={() => {
              if (!fs.hasAllValid) return;

              mutate({
                params: {
                  email: fs.values.email,
                  orgFriendlyName: rVars.org.extract(),
                  eventId: rVars.eid.extract(),
                },
              });
            }}
            fs={fs}
            logo={logo.orDefault("")}
            event={event}
            registrationStartState={registrationStartState}
            registrationDeadlineState={registrationDeadlineState}
            orgName={orgName.orDefault("")}
            orgFriendlyName={rVars.org.orDefault("")}
            isSubmitting={isLoading}
            forwardLoggedInEmail={forwardLoggedInEmail}
          />
          <EmailSendFragment
            hidden={!emailResponse?.emailSent}
            email={fs.values.email}
            logo={logo.orDefault("")}
            orgName={orgName.orDefault("")}
            onSubmit={() => {
              if (!fs.hasAllValid) return;

              mutate({
                params: {
                  email: fs.values.email,
                  orgFriendlyName: rVars.org.extract(),
                  eventId: rVars.eid.extract(),
                },
              });
            }}
            isLoading={isLoading}
            title={event.map((e) => e.name).orDefault("Something went wrong")}
          />
        </RegistrationWrapper>
      </LoadingMask>
    </PageWrapper>
  );
};

interface LoggedInProps {
  email: string;
  onDifferentEmailRegisterClick: () => void;
  eventId: number;
  orgFriendlyName: string;
}
function LoggedIn(props: LoggedInProps) {
  const { mutate: register, isLoading } = useRegisterCandidateForEvent({
    mutation: {
      onSuccess: (data) => {
        if (!data.success) {
          return showToast({
            type: "error",
            message:
              "An unexpected error occurred while registering for the event",
          });
        }

        // TODO: I couldn't get the Spring redirect working
        window.location.href = data.registrationUrl;
      },
      onError: (err) => {
        printDevError(err);
        showToast({
          type: "error",
          message:
            "An unexpected error occurred while registering for the event",
        });
      },
    },
  });

  return (
    <>
      <div className="mb-6 w-full rounded-lg border border-[#D4E2EE] bg-[#F3F6FC] px-2 py-4 text-center">
        <p className="mb-3.5 flex justify-center gap-x-2.5 font-inter text-base font-medium text-charcoal">
          <span className="text-charcoal">
            You&apos;re logged in with Flo Recruit Forward
          </span>
          <FiCheckCircle className="text-success" />
        </p>
        <p className="text-center font-inter text-sm text-charcoal">
          Click &quot;Next&quot; to continue registration as{" "}
          <span className="font-medium">{props.email}</span>. If you&apos;d like
          to register with a different email address,{" "}
          {/* TODO: Reusable component for this link in FloKit */}
          <button
            onClick={props.onDifferentEmailRegisterClick}
            className="font-semibold text-navy hover:underline focus:underline focus:outline-none"
          >
            click here
          </button>
          .
        </p>
      </div>
      <Button
        color="navy"
        onClick={() =>
          register({
            orgFriendlyName: props.orgFriendlyName,
            eventId: props.eventId,
          })
        }
        width="full"
        loading={isLoading}
      >
        Next
      </Button>
      <PrivacyPolicyTermsOfServiceFooter />
    </>
  );
}
